import {
  Box, Grid, TextField, MenuItem, FormControlLabel, Switch, Typography, Chip,
  IconButton, Button,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ErpAmountField from '../ErpAmountField';
import { InfoTip } from '../LabelWithInfo';
import { FORMULA_PROVISIONS, isEmptyAmount, isInvalidAmount } from './quotationUtils';

// Marca de lo que la cotización cambió respecto del acuerdo
const ModifiedChip = ({ show }) => {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <Chip component="span" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20 }}
      label={t('erp.quotations.conditions.modified')} />
  );
};

const labelWithChip = (text, tooltip, modified) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
    {text}
    {tooltip && <InfoTip title={tooltip} />}
    <ModifiedChip show={modified} />
  </Box>
);

/**
 * Condiciones comerciales de la cotización (fase 4 del cotizador OUT/EST,
 * 2026-10-13): margen, margen mínimo, gastos operacionales, gasto de
 * administración, costo de asistencia y provisiones (calculadas y de monto
 * fijo). Vienen del acuerdo del centro de costo (o de todo el cliente) y se
 * pueden cambiar para esta cotización; `modified` marca las que difieren del
 * acuerdo. `value` es el formulario de conditionsToForm (quotationUtils).
 */
const QuotationConditionsEditor = ({ value, onChange, modified = [], showPurchaseOrder = false }) => {
  const { t } = useTranslation();
  const isModified = (key) => modified.includes(key);
  // Monto que no se entiende como número: campo en rojo (el wizard no avanza)
  const amountError = (raw) => (isInvalidAmount(raw)
    ? { error: true, helperText: t('erp.quotations.conditions.invalidAmount') }
    : {});
  // Monto que falta (interruptor encendido o provisión fija con nombre): en rojo
  const missingError = { error: true, helperText: t('erp.quotations.conditions.missingAmount') };
  // Monto de un interruptor: "el de Parámetros" (cotización anterior), falta o no se entiende
  const toggleAmountProps = (toggle) => {
    if (toggle.fromParameters) return { helperText: t('erp.quotations.conditions.fromParametersHint') };
    return isEmptyAmount(toggle.amount) ? missingError : amountError(toggle.amount);
  };

  const set = (field, next) => onChange({ ...value, [field]: next });
  // Tocar el interruptor o el monto deja de usar "los del set" (cotización anterior)
  const setToggle = (field, key, next) => onChange({
    ...value,
    [field]: { ...value[field], [key]: next, fromParameters: false },
  });
  const setFormula = (key, next) => onChange({ ...value, provisions: { ...value.provisions, [key]: next } });
  const setFixed = (index, key, next) => onChange({
    ...value,
    fixedProvisions: value.fixedProvisions.map((p, i) => (i === index ? { ...p, [key]: next } : p)),
  });
  const addFixed = () => onChange({
    ...value,
    fixedProvisions: [...value.fixedProvisions, { name: '', amount: '', prorated: false }],
  });
  const removeFixed = (index) => onChange({
    ...value,
    fixedProvisions: value.fixedProvisions.filter((_, i) => i !== index),
  });

  // Interruptor + monto (gastos operacionales, gasto adm., costo de asistencia)
  const toggleAmount = (field, labelKey, amountLabelKey, tooltipKey) => (
    <Grid item xs={12} sm={4}>
      <FormControlLabel
        control={<Switch checked={Boolean(value[field]?.enabled)}
          onChange={(e) => setToggle(field, 'enabled', e.target.checked)} />}
        label={labelWithChip(t(labelKey), tooltipKey ? t(tooltipKey) : null, isModified(field))}
      />
      {value[field]?.enabled && (
        <ErpAmountField label={t(amountLabelKey)} value={value[field].amount}
          {...toggleAmountProps(value[field])}
          onChange={(e) => setToggle(field, 'amount', e.target.value)} />
      )}
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField select fullWidth size="small" label={t('erp.agreement.margins.feeType')}
          value={value.marginFeeType} onChange={(e) => set('marginFeeType', e.target.value)}>
          <MenuItem value="percentage">{t('erp.agreement.margins.percentage')}</MenuItem>
          <MenuItem value="fixed">{t('erp.agreement.margins.fixed')}</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={3}>
        <ErpAmountField label={t('erp.quotations.conditions.margin')} value={value.marginValue}
          {...amountError(value.marginValue)}
          onChange={(e) => set('marginValue', e.target.value)}
          InputProps={isModified('margin') ? { endAdornment: <ModifiedChip show /> } : undefined} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ErpAmountField label={t('erp.quotations.conditions.marginMinimum')} value={value.marginMinimum}
          helperText={t('erp.quotations.conditions.marginMinimumHint')}
          {...amountError(value.marginMinimum)}
          onChange={(e) => set('marginMinimum', e.target.value)}
          InputProps={isModified('marginMinimum') ? { endAdornment: <ModifiedChip show /> } : undefined} />
      </Grid>

      {toggleAmount('operationalExpenses', 'erp.quotations.conditions.operationalExpenses',
        'erp.quotations.conditions.operationalExpensesAmount', 'erp.tooltips.agreement.operationalExpenses')}
      {toggleAmount('adminExpense', 'erp.agreement.adminExpense', 'erp.agreement.adminExpenseAmount',
        'erp.tooltips.agreement.adminExpense')}
      {toggleAmount('assistanceCost', 'erp.agreement.assistanceCost', 'erp.agreement.assistanceCostAmount',
        'erp.tooltips.agreement.assistanceCost')}

      <Grid item xs={12}>
        <Typography variant="subtitle2">
          {labelWithChip(t('erp.quotations.conditions.provisionsTitle'), t('erp.tooltips.agreement.formulaProvisions'),
            isModified('provisions'))}
        </Typography>
      </Grid>
      {FORMULA_PROVISIONS.map((key) => (
        <Grid item xs={12} sm={4} key={key}>
          <FormControlLabel
            control={<Switch checked={value.provisions?.[key] === true}
              onChange={(e) => setFormula(key, e.target.checked)} />}
            label={labelWithChip(t(`erp.quotations.conditions.formula.${key}`),
              t(`erp.quotations.conditions.formulaHint.${key}`), false)}
          />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary">
          {t('erp.quotations.conditions.fixedTitle')}
          <InfoTip title={t('erp.tooltips.agreement.fixedProvisions')} />
        </Typography>
      </Grid>
      {value.fixedProvisions.map((p, index) => (
        <Grid item xs={12} key={index}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" label={t('erp.quotations.conditions.fixedName')}
                value={p.name} onChange={(e) => setFixed(index, 'name', e.target.value)} />
            </Grid>
            <Grid item xs={7} sm={3}>
              <ErpAmountField label={t('erp.quotations.conditions.fixedAmount')}
                {...(String(p.name || '').trim() && isEmptyAmount(p.amount) ? missingError : amountError(p.amount))}
                value={p.amount} onChange={(e) => setFixed(index, 'amount', e.target.value)} />
            </Grid>
            <Grid item xs={4} sm={3}>
              <FormControlLabel
                control={<Switch size="small" checked={p.prorated === true}
                  onChange={(e) => setFixed(index, 'prorated', e.target.checked)} />}
                label={<Typography variant="body2">{t('erp.quotations.conditions.fixedProrated')}</Typography>}
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" aria-label={t('erp.quotations.conditions.fixedRemove')}
                onClick={() => removeFixed(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid item xs={12} sx={{ pt: '4px !important' }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addFixed} sx={{ textTransform: 'none' }}>
          {t('erp.quotations.conditions.fixedAdd')}
        </Button>
      </Grid>

      {showPurchaseOrder && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={Boolean(value.usesPurchaseOrder)}
              onChange={(e) => set('usesPurchaseOrder', e.target.checked)} />}
            label={t('erp.agreement.usesPurchaseOrder')}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default QuotationConditionsEditor;
